import type { ComboboxOnChangeType } from '@zillow/constellation-10';
import { Box, Card, Icon, IconButton } from '@zillow/constellation-10';
import { IconCloseFilled } from '@zillow/constellation-icons';
import { TourDuration } from '@zg-rentals/ts-z-calendar-client';

import TourAvailabilityCardTimeSlot from './TourAvailabilityCardTimeSlot';
import FormattedDatePickerInput, {
  type DatePickerMetaProps,
} from '../../TourCalendar/components/FormattedDatePickerInput';
import type { AvailabilityDate, TimeSlot } from '../../../types/TourAvailabilityCards';

type TourAvailabilityCardProps = {
  availabilityDate: AvailabilityDate;
  onChangeDate: (date: string, meta: DatePickerMetaProps) => void;
  onRemoveDate: () => void;
  onAddTimeSlot: () => void;
  onChangeTimeSlot: (timeSlot: TimeSlot, field: 'start' | 'end') => ComboboxOnChangeType<string>;
  onRemoveTimeSlot: (index: number) => () => void;
  disabledDates?: Array<string>;
  tourDuration?: TourDuration;
};

const TourAvailabilityCard = ({
  availabilityDate,
  onChangeDate,
  onRemoveDate,
  onAddTimeSlot,
  onChangeTimeSlot,
  onRemoveTimeSlot,
  disabledDates,
  tourDuration = TourDuration.MINUTES_30,
}: TourAvailabilityCardProps) => {
  return (
    <Card elevated={false} data-testid="TourAvailabilityCard">
      <Box css={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: 'tight' }}>
        <FormattedDatePickerInput
          date={availabilityDate.date}
          onChangeDate={onChangeDate}
          disabledDates={disabledDates}
        />

        <IconButton
          emphasis="bare"
          shape="circle"
          size="sm"
          title="Delete date"
          tone="neutral"
          onClick={onRemoveDate}
          data-testid="TourAvailabilityCard-RemoveDateButton"
          css={{ marginRight: '1100' }}
        >
          <Icon>
            <IconCloseFilled />
          </Icon>
        </IconButton>
      </Box>
      {availabilityDate.timeSlots.map((timeSlot, index) => {
        // Create a stable key using timeslot content to ensure React correctly associates DOM elements
        const stableKey = `${timeSlot.start?.getTime() || 'undefined'}-${timeSlot.end?.getTime() || 'undefined'}-${index}`;
        const isTimeSlotRemovalDisabled = availabilityDate.timeSlots.length <= 1;
        return (
          <Box
            key={stableKey}
            css={{ marginBottom: index === availabilityDate.timeSlots.length - 1 ? 'none' : 'default' }}
          >
            <TourAvailabilityCardTimeSlot
              timeSlot={timeSlot}
              onAddTimeSlot={onAddTimeSlot}
              onChangeTimeSlot={onChangeTimeSlot}
              onRemoveTimeSlot={onRemoveTimeSlot(index)}
              isTimeSlotRemovalDisabled={isTimeSlotRemovalDisabled}
              tourDuration={tourDuration}
            />
          </Box>
        );
      })}
    </Card>
  );
};

export default TourAvailabilityCard;
